"use client"

import React, { createElement } from "react"

// Props the Ninetailed <Experience> passes to its `loadingComponent`
// while it's waiting on the personalization API. Mirrors the shape it
// passes to `DefaultExperienceLoadingComponent`:
//   <LoadingComponent {...baseline} component={Component}
//                     experiences={...} passthroughProps={...} />
// (See @ninetailed/experience.js-react Experience implementation.)
interface BaselineLoadingComponentProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: React.ComponentType<any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  passthroughProps?: Record<string, any>
  // Spread baseline fields land here as `[key: string]: any`.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

/**
 * Render the baseline component immediately while the Ninetailed SDK
 * fetches the personalization profile.
 *
 * Replaces Ninetailed's DefaultExperienceLoadingComponent, which holds
 * the baseline at `visibility: hidden` for `unhideAfterMs` (default 5s)
 * before showing it. That default exists to prevent a flash-of-baseline
 * → variant swap, but for our content (where the baseline is always a
 * valid render and the variant is an enhancement) the visible delay is
 * far worse than the swap. This component renders the baseline on the
 * first frame; <Experience> still receives the variant via its own
 * useExperience subscription once the API resolves and re-renders this
 * subtree with the variant in place of the baseline.
 *
 * Pass to <Experience loadingComponent={BaselineLoadingComponent} />.
 */
const BaselineLoadingComponent = ({
  component: Component,
  passthroughProps,
  // The remaining props are the baseline content fields spread by
  // <Experience>. Strip the keys we don't want to forward to the
  // baseline component.
  experiences: _experiences,
  ...baseline
}: BaselineLoadingComponentProps) => {
  return createElement(Component, {
    ...passthroughProps,
    ...baseline,
    ninetailed: {
      isPersonalized: false,
      audience: { id: "baseline" },
    },
  })
}

export default BaselineLoadingComponent
